/**
 * V2Nodes.jsx — Custom React Flow node components for Canvas V2.
 *
 * Every color pulls from `v2Palette.js` which in turn pulls from the Agentlets
 * brand design system (BRAND + ACCENT). This file is the visual identity of
 * Canvas V2 — bright, branded, bookended in terracotta.
 *
 * Node inventory (kind → component):
 *
 *   sentinel          — v2Sentinel          (Start green / End BRAND terracotta)
 *   leaf              — v2Leaf              (scalar task)
 *   switchHead        — v2SwitchHead
 *   switchContainer   — v2SwitchContainer
 *   caseContainer     — v2CaseContainer
 *   meet              — v2Meet              (convergence dot)
 *   terminate         — v2Terminate
 *   subWorkflow       — v2SubWorkflow       (inline scalar when not expanded)
 *   subWorkflowContainer — v2SubWorkflowContainer (inline-expanded — #162)
 *   forkContainer / forkHead / legContainer / join (#161)
 *   dynForkContainer / dynForkHead / dynLegTemplate (#161)
 *   loopContainer / loopHead / loopBody              (#162)
 *
 * Every node supports an optional `data.execStatus` (running | completed |
 * failed | skipped | waiting) that draws an exec-overlay ring around the
 * chrome — this is the #163 scaffolding.
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { V2_PAL, V2_BRAND, V2_STONE } from './v2Palette';
import { NODE_TYPES } from '../../../data/soarNodes';

// ═══════════════════════════════════════════════════════════════════
//  Execution-overlay helper — wraps any node with a status ring.
//  Keeps the ring non-invasive: 2px outline + glow, no layout impact.
// ═══════════════════════════════════════════════════════════════════

function execRingStyle(execStatus) {
  if (!execStatus) return null;
  const spec = V2_PAL.exec[String(execStatus).toLowerCase()];
  if (!spec) return null;
  return {
    boxShadow: `0 0 0 2px ${spec.ring}, 0 0 14px 2px ${spec.glow}`,
  };
}

/* ═══════════════════════════════════════════════════════════════════
 *  G257 / LET_CANVAS_GOLDEN_RULES §4.3 — Truth-in-canvas exec-stats badge.
 *  Reads `data.props.executionStats` (populated by buildAgentSkillsDag
 *  from the policy-audit trail) and renders a compact "N×" pill on the
 *  node showing how often this skill was actually invoked in the last
 *  N days. Empty stats render faint "0×" so the canvas reflects what
 *  the agent ACTUALLY does, not just what was authored.
 *
 *  Badge tones:
 *    count=0 + has-stats  — muted "0×" (skill never invoked in window)
 *    count>0              — green "N×" with last-invoked timestamp on hover
 *    has-blocked          — amber pill ("N× · K blocked")
 *    no stats             — null (not rendered — pre-publish agents)
 * ═══════════════════════════════════════════════════════════════════ */

function formatExecCount(n) {
  if (n == null) return '—';
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n / 1000)}k`;
}

function ExecStatsBadge({ stats }) {
  if (!stats || typeof stats !== 'object') return null;
  const count = stats.count ?? 0;
  const blocked = stats.blocked ?? 0;
  const errors = stats.errors ?? 0;
  const failureCount = blocked + errors;
  const ring = failureCount > 0
    ? { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' }
    : count > 0
      ? { bg: '#dcfce7', border: '#16a34a', text: '#15803d' }
      : { bg: '#f5f5f4', border: '#d6d3d1', text: '#78716c' };
  const p50 = stats.p50;
  const p95 = stats.p95;
  const lastInvoked = stats.lastInvokedAt
    ? new Date(stats.lastInvokedAt).toLocaleString()
    : 'never';
  const tooltip = `Invocations: ${count}${
    failureCount > 0 ? ` (${blocked} blocked, ${errors} errored)` : ''
  }\nLast invoked: ${lastInvoked}${
    p50 != null ? `\nLatency p50/p95: ${p50}ms / ${p95}ms` : ''
  }`;
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3,
        background: ring.bg,
        border: `1px solid ${ring.border}`,
        color: ring.text,
        padding: '0 5px',
        height: 15,
        borderRadius: 7,
        fontSize: 9, fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {formatExecCount(count)}×
      {failureCount > 0 && <span style={{ opacity: 0.85 }}>· {failureCount} fail</span>}
    </span>
  );
}

function ExecBadge({ execStatus, durationMs }) {
  if (!execStatus) return null;
  const spec = V2_PAL.exec[String(execStatus).toLowerCase()];
  if (!spec) return null;
  return (
    <div style={{
      position: 'absolute', top: -9, right: 8,
      padding: '1px 7px',
      background: spec.ring, color: '#fff',
      fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
      borderRadius: 999,
      boxShadow: '0 1px 2px rgba(15,23,42,0.12)',
      whiteSpace: 'nowrap',
    }}>
      {spec.label}{durationMs != null ? ` · ${formatDuration(durationMs)}` : ''}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Diagnostic badge (#166) — top-LEFT stamp that shows lint severity.
//  Rose dot = error, amber = warn, sky = info. Native title= tooltip
//  lists each failing rule's message so the customer can read it
//  without opening a panel. Badge shows the highest severity and the
//  total count when there's more than one.
// ═══════════════════════════════════════════════════════════════════

const DIAG_COLOURS = {
  error: { ring: '#e11d48', glow: 'rgba(225,29,72,0.35)', label: 'ERR' },
  warn:  { ring: '#d97706', glow: 'rgba(217,119,6,0.35)',  label: 'WARN' },
  info:  { ring: '#0284c7', glow: 'rgba(2,132,199,0.30)',  label: 'INFO' },
};

function worstSeverity(diagnostics) {
  let best = null;
  for (const d of diagnostics || []) {
    const s = String(d.severity || '').toLowerCase();
    if (s === 'error') return 'error';
    if (s === 'warn' && best !== 'error') best = 'warn';
    else if (s === 'info' && best == null) best = 'info';
  }
  return best;
}

function DiagnosticBadge({ diagnostics }) {
  if (!diagnostics || diagnostics.length === 0) return null;
  const sev = worstSeverity(diagnostics);
  if (!sev) return null;
  const spec = DIAG_COLOURS[sev];
  const tooltip = diagnostics
    .map((d) => `[${String(d.severity || '').toUpperCase()}] ${d.rule}: ${d.message}`)
    .join('\n');
  return (
    <div
      title={tooltip}
      style={{
        position: 'absolute', top: -9, left: 8,
        padding: '1px 7px',
        background: spec.ring, color: '#fff',
        fontSize: 9, fontWeight: 800, letterSpacing: 0.4,
        borderRadius: 999,
        boxShadow: `0 1px 2px rgba(15,23,42,0.16), 0 0 8px ${spec.glow}`,
        whiteSpace: 'nowrap',
        cursor: 'help',
        zIndex: 2,
      }}
    >
      {spec.label}{diagnostics.length > 1 ? ` · ${diagnostics.length}` : ''}
    </div>
  );
}

function formatDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ═══════════════════════════════════════════════════════════════════
//  Sentinels — Start (emerald) / End (BRAND terracotta)
//  Both render as a 72×72 circle with the SAME label typography.
//  END is *always* rendered — builders synthesize one if missing.
// ═══════════════════════════════════════════════════════════════════

export function V2Sentinel({ data }) {
  const isStart = data && data.kind === 'start';
  const spec = isStart ? V2_PAL.start : V2_PAL.end;
  const execRing = execRingStyle(data?.execStatus);

  // RC-20c (task #1) — START sentinel gets the same per-node "+" the
  // V2Leaf nodes have, so the user can begin authoring a brand-new
  // workflow without hunting for a trigger. END never gets a "+" — it
  // is, by definition, where execution terminates; nothing comes after.
  // The host wires data.onAddAfter to a callback that opens the
  // TaskPicker with the sentinel's nodeId as the insertion anchor.
  const handleAddAfter = (e) => {
    e.stopPropagation();
    if (typeof data?.onAddAfter === 'function' && data?.sourceNodeId) {
      data.onAddAfter(data.sourceNodeId);
    }
  };
  const showPlus = isStart && typeof data?.onAddAfter === 'function';

  return (
    <div style={{
      position: 'relative',
      width: 72, height: 72, borderRadius: '50%',
      background: spec.bgGrad || spec.bg,
      color: spec.text,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 800, letterSpacing: 0.6,
      textTransform: 'uppercase',
      boxShadow: execRing
        ? `${execRing.boxShadow}, 0 2px 6px ${spec.ring}`
        : `0 2px 6px ${spec.ring}`,
      border: `2px solid rgba(255,255,255,0.85)`,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      {isStart ? 'Start' : 'End'}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />

      {/* RC-20c — "+" affordance on START so blank workflows are
          immediately authorable. Same styling as V2Leaf for visual
          continuity. nodrag class keeps ReactFlow from interpreting
          a click as the start of a node drag. */}
      {showPlus && (
        <button
          type="button"
          onClick={handleAddAfter}
          aria-label="Insert first task"
          title="Insert task below (⌘E)"
          className="nodrag v2-node-add-btn"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -12,
            transform: 'translateX(-50%)',
            width: 22, height: 22,
            borderRadius: '50%',
            border: '1.5px solid #ffffff',
            background: '#d97706',
            color: '#ffffff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, lineHeight: 1,
            boxShadow: '0 2px 6px rgba(212, 86, 74, 0.35)',
            zIndex: 5,
          }}
        >+</button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Scalar task leaf — 260 × 72
// ═══════════════════════════════════════════════════════════════════

export function V2Leaf({ data }) {
  const typeLabel = typeLabelFor(data?.canvasType);
  const execRing = execRingStyle(data?.execStatus);
  /* G259 — Specialise visual tone for materialised node types
   * (cognitive_loop / policy_gate_* / framework_control / typed skill)
   * so they read distinctly without needing custom node components. */
  const kindStyle = visualKindStyleFor(data?.canvasType, data?.props?.skillKind);
  // Per-task "+" → opens TaskPicker with kind:'after-node' anchored at THIS
  // task. data.onAddAfter is decorated by V2CanvasView using the host's
  // callback. Stop propagation so the surrounding click doesn't also fire
  // the node-select handler.
  const handleAddAfter = (e) => {
    e.stopPropagation();
    if (typeof data?.onAddAfter === 'function' && data?.sourceNodeId) {
      data.onAddAfter(data.sourceNodeId);
    }
  };
  return (
    <div style={{
      position: 'relative',
      width: 260, minHeight: 72,
      padding: '10px 14px',
      background: V2_PAL.leaf.bg,
      border: `1px solid ${V2_PAL.leaf.border}`,
      /* G259 — Left-edge accent stripe for materialised node types. */
      borderLeft: kindStyle ? `3px solid ${kindStyle.accent}` : `1px solid ${V2_PAL.leaf.border}`,
      borderRadius: 10,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${V2_PAL.leaf.shadow}` : V2_PAL.leaf.shadow,
      transition: 'box-shadow 160ms ease',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      {data?.disconnected && (
        // W133 — orphan canvas node not reachable from Start. flatToIR
        // would normally drop these from the DAG; we render them in a
        // Disconnected zone so the user can see what they have. Amber
        // pill makes the disconnect status visually obvious.
        <div style={{
          position: 'absolute', top: 6, right: 8,
          fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
          color: '#7c2d12', background: '#fef3c7',
          border: '1px solid #f59e0b',
          padding: '2px 6px', borderRadius: 10,
          textTransform: 'uppercase',
        }} title="This task is not connected to the flow. Drag an edge from another task to wire it in.">
          Disconnected
        </div>
      )}
      {data?.cognitiveTool && (
        /* G267 — Cognitive tool catalog. This node is NOT an orphan: the LLM may invoke it
         * at runtime via the loop's availability edge (AGENT_EXECUTION_MODEL §4). Show its
         * governance posture right on the card — a human-gated tool is the whole Prove7
         * story ("autonomy where it's safe, a human gate where it isn't"). */
        <div style={{ position: 'absolute', top: 6, right: 8, display: 'flex', gap: 4 }}>
          {data?.approvalRequired && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
              color: '#7f1d1d', background: '#fee2e2', border: '1px solid #ef4444',
              padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase',
            }} title="Human approval required before this tool can execute.">
              Human gate
            </span>
          )}
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
            color: '#1e3a8a', background: '#dbeafe', border: '1px solid #60a5fa',
            padding: '2px 6px', borderRadius: 10, textTransform: 'uppercase',
          }} title="Tool available to the Cognitive Loop — the LLM decides whether to invoke it at runtime.">
            May invoke
          </span>
        </div>
      )}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 4,
      }}>
        {/* G259 — Type chip tinted per materialised node type. */}
        <span style={{
          fontSize: 10, fontWeight: 700,
          color:      kindStyle ? kindStyle.tagFg : V2_PAL.leaf.typeTag,
          background: kindStyle ? kindStyle.tagBg : 'transparent',
          padding:    kindStyle ? '1px 6px' : 0,
          borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>{typeLabel}</span>
        {/* G257 §4.3 — truth-in-canvas exec count badge */}
        <ExecStatsBadge stats={data?.props?.executionStats} />
      </div>
      <div style={{
        fontSize: 13, color: V2_PAL.leaf.text, fontWeight: 600,
        lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{data?.label || data?.sourceNodeId || 'Task'}</div>
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
      {/* G267c — Dedicated RIGHT-side anchor for the cognitive tool-catalog edge.
          Without it, ReactFlow anchors that edge at the loop's BOTTOM source handle (the
          only one available), so the route dropped out of the loop, doubled back up and
          ran horizontally across the trunk — which is what made it look like the edge was
          coming out of the JOIN node above. Anchoring on the right lets the catalog edge
          leave sideways as a clean horizontal. Rendered ONLY for cognitive_loop, so every
          other leaf's handle geometry is byte-for-byte unchanged. */}
      {data?.canvasType === 'cognitive_loop' && (
        <Handle
          id="cog-out"
          type="source"
          position={Position.Right}
          style={handleStyle({ invisible: true })}
        />
      )}
      {/* Bottom-center "+" — discoverable affordance for "insert below
          this task". Uses absolute positioning so it floats just under
          the card without affecting layout / handle geometry. Hidden
          when the host hasn't wired onAddAfter (e.g. /canvas-v2 read-
          only viewers). Brand red so it pops without competing with the
          handle dot. nodrag class tells ReactFlow to ignore drags
          starting on this button. */}
      {typeof data?.onAddAfter === 'function' && (
        <button
          type="button"
          onClick={handleAddAfter}
          aria-label="Insert task below"
          title="Insert task below (⌘E)"
          className="nodrag v2-node-add-btn"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -12,
            transform: 'translateX(-50%)',
            width: 22, height: 22,
            borderRadius: '50%',
            border: '1.5px solid #ffffff',
            background: '#d97706',
            color: '#ffffff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 700, lineHeight: 1,
            boxShadow: '0 2px 6px rgba(212, 86, 74, 0.35)',
            zIndex: 5,
          }}
        >+</button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Switch — amber family
// ═══════════════════════════════════════════════════════════════════

export function V2SwitchHead({ data }) {
  const s = V2_PAL.switch.head;
  const execRing = execRingStyle(data?.execStatus);
  return (
    <div style={{
      position: 'relative',
      width: 260, height: 96,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 4,
      }}>{switchEyebrowFor(data?.canvasType)}</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{data?.label || 'Route'}</div>
      {data?.expression && (
        <div style={{
          fontSize: 10, color: s.accent, marginTop: 4,
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{data.expression}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

export function V2SwitchContainer({ data }) {
  const c = V2_PAL.switch.container;
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1.5px dashed ${c.border}`,
      borderRadius: 14,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={containerChipStyle(c)}>{switchEyebrowFor(data?.canvasType)} · {data?.label || ''}</div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2CaseContainer({ data }) {
  const isDefault = !!data?.isDefault;
  const c = V2_PAL.switch.caseContainer;
  const bg     = isDefault ? c.defaultBg : c.bg;
  const border = isDefault ? c.defaultBorder : c.border;
  const chipBg = isDefault ? c.defaultChipBg : c.chipBg;
  const chipText = isDefault ? c.defaultChipText : c.chipText;
  const chipBorder = isDefault ? c.defaultBorder : c.chipBorder;
  // Canvas V4 Phase 2b — per-branch "+" button. Opt-in via data.onAddAfter
  // (V4CanvasView decorates case containers; V3 does not pass this prop, so
  // V3 renders unchanged). Click fires onAddAfter with the branch context
  // (`{kind:'branch', ownerSwitchId, branchName}`) so the host can resolve
  // the branch edge and open the picker.
  const handleAddInCase = (e) => {
    e.stopPropagation();
    if (typeof data?.onAddAfter === 'function') {
      data.onAddAfter({
        kind: 'branch',
        ownerNodeId: data.ownerSwitchId,
        branchName: data.branchName,
        isDefault,
      });
    }
  };
  return (
    <div style={{
      width: '100%', height: '100%',
      background: bg,
      border: `1px dotted ${border}`,
      borderRadius: 10,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        position: 'absolute', top: -9, left: 10,
        padding: '1px 7px',
        background: chipBg,
        color: chipText,
        fontSize: 10, fontWeight: 700,
        border: `1px solid ${chipBorder}`,
        borderRadius: 4, whiteSpace: 'nowrap',
      }}>case: {data?.branchName || ''}{isDefault ? ' (default)' : ''}</div>
      {typeof data?.onAddAfter === 'function' && (
        <button
          type="button"
          onClick={handleAddInCase}
          aria-label={`Insert task in case ${data?.branchName || ''}`}
          title={`Insert task in case "${data?.branchName || ''}" (⌘E)`}
          className="nodrag v2-node-add-btn"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -11,
            transform: 'translateX(-50%)',
            width: 20, height: 20,
            borderRadius: '50%',
            border: '1.5px solid #ffffff',
            background: '#d97706',
            color: '#ffffff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, lineHeight: 1,
            boxShadow: '0 1px 4px rgba(212, 86, 74, 0.35)',
            zIndex: 5,
          }}
        >+</button>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Meet — the tiny convergence dot at the tail of a switch
// ═══════════════════════════════════════════════════════════════════

export function V2Meet() {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: V2_PAL.meet.bg,
      border: `2px solid ${V2_PAL.meet.border}`,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Terminate — pill, no icon (Rule 0.S3.a)
// ═══════════════════════════════════════════════════════════════════

export function V2Terminate({ data }) {
  const status = (data?.status || 'COMPLETED').toUpperCase();
  const failed = status === 'FAILED';
  const t = failed ? V2_PAL.terminate.failed : V2_PAL.terminate.completed;
  return (
    <div style={{
      position: 'relative',
      width: 260, minHeight: 60,
      padding: '8px 14px',
      background: t.bg,
      border: `1.5px solid ${t.border}`,
      borderRadius: 999,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800,
        color: t.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>{failed ? 'Terminate · FAILED' : 'Terminate'}</div>
      <div style={{
        fontSize: 12, color: t.text,
        fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{data?.label || data?.reason || ''}</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  SubWorkflow — sync / async scalar, plus an inline-expanded container
//  variant added in #162 so customers can drill into nested workflows
//  without leaving the page.
// ═══════════════════════════════════════════════════════════════════

export function V2SubWorkflow({ data }) {
  const isAsync = !!data?.async;
  const s = isAsync ? V2_PAL.subWorkflow.async : V2_PAL.subWorkflow.sync;
  const execRing = execRingStyle(data?.execStatus);
  return (
    <div style={{
      position: 'relative',
      width: 260, minHeight: 88,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 10,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800,
        color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 4,
      }}>{isAsync ? 'Start Workflow (async)' : 'Sub-Workflow'}</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{data?.label || data?.refName || 'Sub-workflow'}</div>
      {data?.refName && data?.refName !== data?.label && (
        <div style={{
          fontSize: 10, color: V2_STONE[500],
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>ref: {data.refName}{data.refVersion != null ? `@${data.refVersion}` : ''}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

// Inline-expanded SubWorkflow — container chrome that holds the embedded
// workflow's nodes. Rendered when the IR carries an `embedded` body.
export function V2SubWorkflowContainer({ data }) {
  const c = V2_PAL.subWorkflow.container;
  const refName = data?.refName ? ` → ${data.refName}${data?.refVersion != null ? `@${data.refVersion}` : ''}` : '';
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1.5px dashed ${c.border}`,
      borderRadius: 14,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={containerChipStyle(c)}>
        Sub-workflow{refName} · inline
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2SubWorkflowHead({ data }) {
  const s = V2_PAL.subWorkflow.sync;
  const execRing = execRingStyle(data?.execStatus);
  return (
    <div style={{
      position: 'relative',
      width: 260, height: 80,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4,
      }}>Sub-Workflow · enter</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{data?.label || 'Sub-workflow'}</div>
      {data?.refName && (
        <div style={{
          fontSize: 10, color: s.accent, marginTop: 2,
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>ref: {data.refName}{data.refVersion != null ? `@${data.refVersion}` : ''}</div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  FORK FAMILY (#161) — teal (static) vs violet (dynamic)
// ═══════════════════════════════════════════════════════════════════

export function V2ForkContainer({ data }) {
  const c = V2_PAL.fork.container;
  const legCount = data?.legCount ?? 0;
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1.5px dashed ${c.border}`,
      borderRadius: 14,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={containerChipStyle(c)}>
        {/* RC-112e — say "branches" not "legs" to match the schema field
         * (parallel_fork.configSchema declares `branches`). Internal
         * variable name `legCount` retained for back-compat with the
         * V2 renderer + ELK layout — that's algorithm naming, not
         * operator-facing. Only the visible string changed. */}
        Fork · {data?.label || ''}{legCount > 0 ? ` · ${legCount} ${legCount === 1 ? 'branch' : 'branches'}` : ''}
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2ForkHead({ data }) {
  const s = V2_PAL.fork.head;
  const legCount = data?.legCount ?? 0;
  const execRing = execRingStyle(data?.execStatus);
  return (
    <div style={{
      position: 'relative',
      width: 260, height: 80,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 4,
      }}>Fork · parallel</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{data?.label || 'Fork'}</div>
      {legCount > 0 && (
        <div style={{ fontSize: 10, color: s.accent, marginTop: 2 }}>
          {legCount} branch{legCount === 1 ? '' : 'es'}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

export function V2LegContainer({ data }) {
  // RC-112e — Internal name V2LegContainer kept for back-compat (callers
  // in flatToElkHierarchical / ELK layout reference it by name). Only
  // user-visible strings switched from "leg X" → "branch X" to align
  // with the schema field name (`branches` in parallel_fork.configSchema).
  const c = V2_PAL.fork.legContainer;
  const legIndex = Number.isInteger(data?.legIndex) ? data.legIndex : null;
  const legTag = legIndex != null ? `branch ${legIndex + 1}` : 'branch';
  const name = data?.branchName || '';
  // Canvas V4 Phase 2b — per-leg "+" button. Same opt-in pattern as the
  // case container: V4 decorates with data.onAddAfter; V3 does not.
  const handleAddInLeg = (e) => {
    e.stopPropagation();
    if (typeof data?.onAddAfter === 'function') {
      data.onAddAfter({
        kind: 'branch',
        ownerNodeId: data.ownerForkId,
        branchName: data.branchName,
        legIndex,
      });
    }
  };
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1px dotted ${c.border}`,
      borderRadius: 10,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        position: 'absolute', top: -9, left: 10,
        padding: '1px 7px',
        background: c.chipBg, color: c.chipText,
        fontSize: 10, fontWeight: 700,
        border: `1px solid ${c.chipBorder}`,
        borderRadius: 4, whiteSpace: 'nowrap',
      }}>{legTag}{name ? ` · ${name}` : ''}</div>
      {typeof data?.onAddAfter === 'function' && (
        <button
          type="button"
          onClick={handleAddInLeg}
          aria-label={`Insert task in ${legTag}`}
          title={`Insert task in ${legTag}${name ? ` (${name})` : ''} (⌘E)`}
          className="nodrag v2-node-add-btn"
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -11,
            transform: 'translateX(-50%)',
            width: 20, height: 20,
            borderRadius: '50%',
            border: '1.5px solid #ffffff',
            background: '#d97706',
            color: '#ffffff',
            cursor: 'pointer',
            padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, lineHeight: 1,
            boxShadow: '0 1px 4px rgba(212, 86, 74, 0.35)',
            zIndex: 5,
          }}
        >+</button>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2Join({ data }) {
  const mode = (data?.waitMode || 'ALL').toUpperCase();
  const count = data?.waitCount;
  const legCount = data?.legCount;
  const dynamic = !!data?.dynamic;
  const execRing = execRingStyle(data?.execStatus);

  let badge = '●', badgeHint = 'wait for all';
  if (mode === 'ANY' || mode === 'ANY_COMPLETED') { badge = '◐'; badgeHint = 'wait for any'; }
  if (mode === 'N_OF_M' && count && legCount) { badge = `${count}/${legCount}`; badgeHint = `wait for ${count} of ${legCount}`; }

  const border = dynamic ? V2_PAL.join.dynamicBorder : V2_PAL.join.staticBorder;
  const bg     = dynamic ? V2_PAL.join.dynamicBg     : V2_PAL.join.staticBg;
  const fg     = dynamic ? V2_PAL.join.dynamicText   : V2_PAL.join.staticText;
  const ring   = dynamic ? V2_PAL.join.dynamicRing   : V2_PAL.join.staticRing;

  return (
    <div style={{
      position: 'relative',
      width: 260, height: 80,
      padding: '8px 12px',
      background: bg,
      border: `1.5px solid ${border}`,
      borderRadius: 14,
      display: 'flex', alignItems: 'center', gap: 10,
      boxShadow: execRing ? `${execRing.boxShadow}, 0 1px 3px rgba(15,23,42,0.08)` : '0 1px 3px rgba(15,23,42,0.08)',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div
        title={badgeHint}
        style={{
          minWidth: 32, height: 32, padding: '0 6px',
          borderRadius: 16, background: ring, color: '#ffffff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: mode === 'N_OF_M' ? 11 : 16, fontWeight: 800,
          flexShrink: 0,
      }}>{badge}</div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 10, fontWeight: 800, color: fg,
          textTransform: 'uppercase', letterSpacing: 0.6,
          marginBottom: 2,
        }}>Join · wait {mode.toLowerCase()}</div>
        <div style={{
          fontSize: 13, color: V2_STONE[900], fontWeight: 700,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{data?.label || 'Join'}</div>
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

export function V2DynForkContainer({ data }) {
  const c = V2_PAL.dynFork.container;
  const expr = data?.listExpression || '';
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1.5px dashed ${c.border}`,
      borderRadius: 14,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        ...containerChipStyle(c), whiteSpace: 'nowrap',
      }}>
        Dynamic Fork · {data?.label || ''}
        {expr ? <span style={{ fontFamily: 'SFMono-Regular, Menlo, monospace', textTransform: 'none', marginLeft: 6, opacity: 0.85 }}>{expr}</span> : null}
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2DynForkHead({ data }) {
  const s = V2_PAL.dynFork.head;
  const execRing = execRingStyle(data?.execStatus);
  const expr = data?.listExpression || null;
  const cap = data?.concurrencyLimit;
  return (
    <div style={{
      position: 'relative',
      width: 280, minHeight: 104,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 4,
      }}>Dynamic Fork · fan-out</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{data?.label || 'Dynamic Fork'}</div>
      {expr && (
        <div style={{
          fontSize: 10, color: s.accent, marginTop: 4,
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>for each → {expr}</div>
      )}
      {cap != null && (
        <div style={{ fontSize: 10, color: s.accent, marginTop: 2 }}>
          max parallel: {cap}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

export function V2DynLegTemplate({ data }) {
  const t = V2_PAL.dynFork.templateLeg;
  const name = data?.branchName || 'item';
  return (
    <div style={{
      width: '100%', height: '100%',
      background: `repeating-linear-gradient(45deg, ${t.stripeA}, ${t.stripeA} 6px, ${t.stripeB} 6px, ${t.stripeB} 12px)`,
      border: `1.5px dashed ${t.border}`,
      borderRadius: 10,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        position: 'absolute', top: -9, left: 10,
        padding: '1px 7px',
        background: t.chipBg, color: t.chipText,
        fontSize: 10, fontWeight: 800,
        border: `1px solid ${t.chipBorder}`,
        borderRadius: 4,
        textTransform: 'uppercase', letterSpacing: 0.5,
        whiteSpace: 'nowrap',
      }}>template · {name}</div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  LOOP / DO_WHILE (#162) — orange family
//  Container has a head (the loop definition) and a body container with
//  the repeating tasks. The self-loop back-edge (dashed orange) is drawn
//  by the V2Edge renderer based on edge variant 'loopBack'.
// ═══════════════════════════════════════════════════════════════════

export function V2LoopContainer({ data }) {
  const c = V2_PAL.loop.container;
  const cond = data?.condition || '';
  const iterHint = data?.iterationHint || '';
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1.5px dashed ${c.border}`,
      borderRadius: 14,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        ...containerChipStyle(c), whiteSpace: 'nowrap', maxWidth: '95%',
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        Loop · {data?.label || ''}
        {iterHint ? <span style={{ textTransform: 'none', opacity: 0.85, marginLeft: 6 }}>{iterHint}</span> : null}
        {cond && !iterHint ? <span style={{ fontFamily: 'SFMono-Regular, Menlo, monospace', textTransform: 'none', marginLeft: 6, opacity: 0.85 }}>while {cond}</span> : null}
      </div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

export function V2LoopHead({ data }) {
  const s = V2_PAL.loop.head;
  const execRing = execRingStyle(data?.execStatus);
  const iter = data?.currentIteration != null && data?.maxIterations != null
    ? `${data.currentIteration}/${data.maxIterations}` : null;
  return (
    <div style={{
      position: 'relative',
      width: 260, height: 88,
      padding: '10px 14px',
      background: s.bg,
      border: `1.5px solid ${s.border}`,
      borderRadius: 14,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: execRing ? `${execRing.boxShadow}, ${s.shadow}` : s.shadow,
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <ExecBadge execStatus={data?.execStatus} durationMs={data?.execDurationMs} />
      <DiagnosticBadge diagnostics={data?.diagnostics} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: s.accent,
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 4,
      }}>Loop · do-while</div>
      <div style={{
        fontSize: 13, color: s.text, fontWeight: 700,
        lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{data?.label || 'Loop'}</div>
      {data?.condition && (
        <div style={{
          fontSize: 10, color: s.accent, marginTop: 4,
          fontFamily: 'SFMono-Regular, Menlo, monospace',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>while {data.condition}</div>
      )}
      {iter && (
        <div style={{ fontSize: 10, color: s.accent, marginTop: 2, fontWeight: 700 }}>
          iter {iter}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

export function V2LoopBody({ data }) {
  const c = V2_PAL.loop.bodyContainer;
  return (
    <div style={{
      width: '100%', height: '100%',
      background: c.bg,
      border: `1px dotted ${c.border}`,
      borderRadius: 10,
      position: 'relative',
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle({ invisible: true })} />
      <div style={{
        position: 'absolute', top: -9, left: 10,
        padding: '1px 7px',
        background: c.chipBg, color: c.chipText,
        fontSize: 10, fontWeight: 700,
        border: `1px solid ${c.chipBorder}`,
        borderRadius: 4, whiteSpace: 'nowrap',
      }}>body{data?.label ? ` · ${data.label}` : ''}</div>
      <Handle type="source" position={Position.Bottom} style={handleStyle({ invisible: true })} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Stub — reserved for future constructs we haven't built yet
// ═══════════════════════════════════════════════════════════════════

export function V2Stub({ data }) {
  const kind = data?.kind || 'stub';
  return (
    <div style={{
      width: 260, minHeight: 72,
      padding: '10px 14px',
      background: '#f8fafc',
      border: `1.5px dashed ${V2_STONE[400]}`,
      borderRadius: 10,
      color: V2_STONE[600],
    }}>
      <Handle type="target" position={Position.Top} style={handleStyle()} />
      <div style={{
        fontSize: 10, fontWeight: 800, color: V2_STONE[700],
        textTransform: 'uppercase', letterSpacing: 0.6,
      }}>{kind} · coming soon</div>
      <div style={{
        fontSize: 13, color: V2_STONE[900], fontWeight: 600,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{data?.label || data?.sourceNodeId || kind}</div>
      <Handle type="source" position={Position.Bottom} style={handleStyle()} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Shared styling helpers
// ═══════════════════════════════════════════════════════════════════

function containerChipStyle(c) {
  return {
    position: 'absolute', top: -11, left: 14,
    padding: '3px 9px',
    background: c.chipBg, color: c.chipText,
    fontSize: 10, fontWeight: 800,
    border: `1px solid ${c.chipBorder}`,
    borderRadius: 5,
    textTransform: 'uppercase', letterSpacing: 0.6,
    boxShadow: '0 1px 2px rgba(15,23,42,0.06)',
  };
}

function handleStyle({ invisible = false } = {}) {
  return {
    width: 1, height: 1,
    background: invisible ? 'transparent' : V2_STONE[500],
    border: 'none',
    opacity: invisible ? 0 : 0.5,
  };
}

/* C1 — What to call a Switch container whose source node is not a condition.
 *
 * A Switch is the IR for "exactly one arm runs", and a condition node is not
 * the only thing shaped that way: a policy gate, a DPG scan, an approval and
 * an RBAC check all pick one arm from a verdict rather than from an
 * expression. Since the trunk walk started routing every declared out-port,
 * those render as Switch containers too — and were all announcing themselves
 * as "Switch · route", so an operator looking for the Data Protection Gate on
 * their canvas found a generic router.
 *
 * The name comes from the registry, which is where every other operator-facing
 * node name comes from, rather than from a second list here that would drift
 * the first time a gate is added. Falls back to the old wording for a real
 * condition node and for anything the registry has never heard of, so nothing
 * that renders today changes.
 */
function switchEyebrowFor(canvasType) {
  if (!canvasType || canvasType === 'condition') return 'Switch · route';
  const entry = NODE_TYPES[canvasType];
  return (entry && entry.label) || typeLabelFor(canvasType);
}

function typeLabelFor(canvasType) {
  if (!canvasType) return 'Task';
  const map = {
    http_task: 'HTTP',
    script_task: 'Script',
    script: 'Script',
    data: 'Data',
    email: 'Email',
    transform: 'Transform',
    webhook: 'Webhook',
    agent: 'Agent',
    ai_prompt: 'AI Prompt',
    ai: 'AI',
    sub_workflow: 'Sub-workflow',
    task: 'Task',
    /* G259 / LET_CANVAS_GOLDEN_RULES — materialised node-type labels.
     * These come out of buildAgentSkillsDag and need readable display
     * names. The generic `standard` is treated as a Skill placeholder
     * when it carries an agentSkillId (visualKindStyleFor() handles the
     * tone shift). */
    standard:               'Skill',
    cognitive_loop:         'Cognitive Loop',
    policy_gate_org:        'Policy · Org',
    policy_gate_tenant:     'Policy · Tenant',
    policy_gate_agent:      'Policy · Agent',
    policy_gate_skill_pre:  'Policy · Skill (pre)',
    policy_gate_skill_post: 'Policy · Skill (post)',
    framework_control:      'Framework',
  };
  return map[canvasType] || canvasType;
}

/* G259 — Per-canvasType visual tone. Tints the type-label chip + the
 * node border so cognitive_loop / policy_gate_* / framework_control
 * read distinctly at a glance, matching LET_CANVAS_GOLDEN_RULES intent
 * to specialise materialised nodes visually. Operator-authored nodes
 * (decision/branch/loop/wait/etc) keep the default V2_PAL.leaf tone.
 *
 * Returns { tagBg, tagFg, accent } — the leaf renderer overlays these
 * on the existing tag chip + a 2px left-border accent.
 */
function visualKindStyleFor(canvasType, skillKind) {
  switch (canvasType) {
    case 'cognitive_loop':
      return { tagBg: '#f3e8ff', tagFg: '#6b21a8', accent: '#8b5cf6' };
    case 'policy_gate_org':
      return { tagBg: '#fee2e2', tagFg: '#991b1b', accent: '#dc2626' };
    case 'policy_gate_tenant':
      return { tagBg: '#fed7aa', tagFg: '#9a3412', accent: '#ea580c' };
    case 'policy_gate_agent':
      return { tagBg: '#fef3c7', tagFg: '#92400e', accent: '#d97706' };
    case 'policy_gate_skill_pre':
    case 'policy_gate_skill_post':
      return { tagBg: '#e0f2fe', tagFg: '#075985', accent: '#0284c7' };
    case 'framework_control':
      return { tagBg: '#dcfce7', tagFg: '#14532d', accent: '#16a34a' };
    case 'standard':
      /* Materialised skills carry skillKind ∈ {standard, workflow_skill,
       * connector_skill, policy_skill}. Tint by kind so workflow vs
       * connector vs policy skills read distinctly. Plain `standard`
       * keeps the default neutral tone. */
      if (skillKind === 'workflow_skill') return { tagBg: '#e0f2fe', tagFg: '#075985', accent: '#0ea5e9' };
      if (skillKind === 'connector_skill') return { tagBg: '#ede9fe', tagFg: '#5b21b6', accent: '#7c3aed' };
      if (skillKind === 'policy_skill')   return { tagBg: '#fef3c7', tagFg: '#92400e', accent: '#d97706' };
      return null;
    default:
      return null;
  }
}

/**
 * V2ToolCatalog — the dotted envelope around a cognitive loop's tool catalog.
 *
 * G267c. The tools a cognitive loop MAY invoke are not a sequence and not a fan-out: the
 * LLM picks them at runtime. Painting one edge per tool (even bidirectional, even in its
 * own lane) still produced N routes crossing the trunk and each other. The honest picture
 * is a SET — so we draw the set as a set: one soft dotted rectangle enclosing the tool
 * cards, with a single dashed edge from the loop to the group.
 *
 * This is chrome only. It carries no canvas node, is never persisted, and is invisible to
 * flatToIR / the validators / the backend — V2CanvasView synthesises it at render time from
 * the `cognitive_avail` edges that already exist in the data.
 */
export function V2ToolCatalog({ data }) {
  const count = data?.toolCount ?? 0;
  return (
    <div style={{
      width: '100%', height: '100%',
      background: 'rgba(8,145,178,0.035)',
      border: '1.5px dashed #67e8f9',
      borderRadius: 16,
      position: 'relative',
    }}>
      {/* Left-edge anchor for the single loop → catalog edge. */}
      <Handle id="cog-in" type="target" position={Position.Left} style={handleStyle({ invisible: true })} />
      <div style={{
        position: 'absolute', top: -10, left: 14,
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '2px 10px',
        background: '#ecfeff',
        border: '1px solid #67e8f9',
        borderRadius: 999,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
        textTransform: 'uppercase', color: '#0e7490',
        whiteSpace: 'nowrap',
      }}>
        May invoke · LLM decides
        <span style={{
          padding: '0 5px', borderRadius: 999,
          background: '#0891b2', color: '#fff', fontSize: 9,
        }}>{count}</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  Node-type map exported to <ReactFlow nodeTypes={...} />
// ═══════════════════════════════════════════════════════════════════

export const V2_NODE_TYPES = {
  // Cognitive tool catalog (#G267c) — render-time chrome, no canvas node.
  v2ToolCatalog:         V2ToolCatalog,
  v2Sentinel:            V2Sentinel,
  v2Leaf:                V2Leaf,
  v2SwitchHead:          V2SwitchHead,
  v2SwitchContainer:     V2SwitchContainer,
  v2CaseContainer:       V2CaseContainer,
  v2Meet:                V2Meet,
  v2Terminate:           V2Terminate,
  v2SubWorkflow:         V2SubWorkflow,
  v2SubWorkflowContainer: V2SubWorkflowContainer,
  v2SubWorkflowHead:     V2SubWorkflowHead,
  // Fork family (#161)
  v2ForkContainer:       V2ForkContainer,
  v2ForkHead:            V2ForkHead,
  v2LegContainer:        V2LegContainer,
  v2Join:                V2Join,
  v2DynForkContainer:    V2DynForkContainer,
  v2DynForkHead:         V2DynForkHead,
  v2DynLegTemplate:      V2DynLegTemplate,
  // Loop family (#162)
  v2LoopContainer:       V2LoopContainer,
  v2LoopHead:            V2LoopHead,
  v2LoopBody:            V2LoopBody,
  // Catch-all
  v2Stub:                V2Stub,
};
